import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";

import MenuBar from "../../../components/MenuBar";
import {
  ContentBatchRow,
  ContentDifficulty,
  ContentDifficultyMix,
} from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { SearchableSelect } from "../SearchableSelect";
import { categoryLabel, useCategories } from "../useCategories";
import {
  effectiveGenerationStatus,
  generationFailureText,
} from "../batchStatus";
import {
  DEFAULT_DIFFICULTY_MIX,
  DIFFICULTY_ORDER,
  rebalanceMix,
} from "./difficultyMix";
import { GenerationProgress } from "./GenerationProgress";

const MIN_REQUESTED = 1;
const MAX_REQUESTED = 200;
const POLL_INTERVAL_MS = 3_000;

export const ContentGeneratePage: React.FC = () => {
  const history = useHistory();
  const { categories, reload: reloadCategories } = useCategories();

  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [requested, setRequested] = useState("20");
  const [mix, setMix] = useState<ContentDifficultyMix>(DEFAULT_DIFFICULTY_MIX);
  const [categoryId, setCategoryId] = useState("");
  const [proposing, setProposing] = useState(false);
  const [newCategory, setNewCategory] = useState({ group: "", name: "" });
  const [submitting, setSubmitting] = useState(false);

  /** The run started from this page, polled until terminal. */
  const [batch, setBatch] = useState<ContentBatchRow | null>(null);
  const latestLoad = useRef(0);

  useEffect(
    () => () => {
      latestLoad.current = -1;
    },
    [],
  );

  const count = Number(requested);
  const countValid =
    Number.isInteger(count) && count >= MIN_REQUESTED && count <= MAX_REQUESTED;
  const canSubmit =
    !submitting &&
    !batch &&
    name.trim().length > 0 &&
    prompt.trim().length > 0 &&
    countValid;

  const status = effectiveGenerationStatus(batch?.generation);

  const refresh = useCallback(async (batchId: string) => {
    const loadId = ++latestLoad.current;
    try {
      const fresh = await Content.getBatch(batchId);
      if (loadId === latestLoad.current) {
        setBatch(fresh);
      }
    } catch (error) {
      // Silent: the run is unaffected by a failed poll, and the Drafts list is
      // always there as the durable view. Failing loudly every 3s would be worse.
    }
  }, []);

  useEffect(() => {
    if (!batch || status !== "running") {
      return undefined;
    }
    const timer = setInterval(() => void refresh(batch.id), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [batch, status, refresh]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);
    try {
      const created = await Content.generateBatch({
        name: name.trim(),
        prompt: prompt.trim(),
        requested: count,
        // Absent means every non-hidden category is eligible.
        categoryIds: categoryId ? [categoryId] : undefined,
        difficultyMix: mix,
      });
      setBatch(created);
    } catch (error) {
      // The platform refuses when three runs are already in flight; that
      // message is the useful one, so surface it rather than a generic error.
      toast.error((error as Error).message || "Could not start generation");
    } finally {
      setSubmitting(false);
    }
  };

  const generateAgain = () => {
    latestLoad.current += 1;
    setBatch(null);
    setName("");
    setPrompt("");
  };

  const proposeCategory = async () => {
    const group = newCategory.group.trim();
    const categoryName = newCategory.name.trim();
    if (!group || !categoryName) {
      return;
    }

    try {
      const created = await Content.createCategory({
        group,
        name: categoryName,
      });
      await reloadCategories();
      setCategoryId(created.id);
      setProposing(false);
      setNewCategory({ group: "", name: "" });
      toast.success(`Created ${group}:${categoryName}`);
    } catch (error) {
      toast.error((error as Error).message || "Could not create the category");
    }
  };

  const matched = categories.find((category) => category.id === categoryId);

  return (
    <>
      <MenuBar />
      <Row>
        <Col lg={9}>
          <Card className="mb-3">
            <CardHeader>
              <h2 className="h4 mb-1">Generate</h2>
              <span className="text-muted">
                Draft a named batch with AI, then QA and review.
              </span>
            </CardHeader>
            <CardBody>
              <Form onSubmit={submit}>
                {/* Category first: it constrains what gets generated, so it is
                    chosen before the set is named rather than in a side panel. */}
                <div className="mb-3 pb-3 border-bottom">
                  <h6 className="font-weight-bold text-uppercase small text-muted mb-2">
                    <i className="fas fa-bullseye mr-2" />
                    Category fit
                  </h6>
                  {proposing ? (
                    <>
                      <FormGroup>
                        <Label for="new-category-group">Group</Label>
                        <Input
                          id="new-category-group"
                          bsSize="sm"
                          value={newCategory.group}
                          placeholder="Sports"
                          onChange={(e) =>
                            setNewCategory({
                              ...newCategory,
                              group: e.target.value,
                            })
                          }
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label for="new-category-name">Name</Label>
                        <Input
                          id="new-category-name"
                          bsSize="sm"
                          value={newCategory.name}
                          placeholder="World Cup"
                          onChange={(e) =>
                            setNewCategory({
                              ...newCategory,
                              name: e.target.value,
                            })
                          }
                        />
                      </FormGroup>
                      <Button
                        size="sm"
                        color="primary"
                        className="mr-2"
                        disabled={
                          !newCategory.group.trim() || !newCategory.name.trim()
                        }
                        onClick={proposeCategory}
                      >
                        Create and use
                      </Button>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => setProposing(false)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-muted mb-2">
                        {matched
                          ? "Matched existing category"
                          : "Choose a category"}
                      </p>
                      <SearchableSelect
                        id="fit-category"
                        value={categoryId}
                        onChange={setCategoryId}
                        emptyOption="Any visible category"
                        options={categories.map((category) => ({
                          value: category.id,
                          label: categoryLabel(categories, category.id),
                        }))}
                      />

                      {matched ? (
                        <Alert color="info" className="mt-3 mb-2 py-2 small">
                          <strong>
                            {categoryLabel(categories, matched.id)}
                          </strong>{" "}
                          — {matched.questionCount} existing question
                          {matched.questionCount === 1 ? "" : "s"}. New
                          questions will be added here.
                        </Alert>
                      ) : (
                        <FormText className="d-block mt-3 mb-2">
                          Leave unset to draw from every visible category.
                        </FormText>
                      )}

                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => setProposing(true)}
                      >
                        <i className="fas fa-plus mr-1" />
                        Propose a new category instead
                      </Button>
                    </>
                  )}
                </div>

                <FormGroup>
                  <Label for="batch-name">
                    Batch name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="batch-name"
                    value={name}
                    maxLength={120}
                    disabled={!!batch}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. World Cup — Jul 2026"
                  />
                  <FormText>
                    Campaign-style name — this is the unit you'll publish.
                  </FormText>
                </FormGroup>

                <FormGroup>
                  <Label for="batch-prompt">Prompt</Label>
                  <Input
                    id="batch-prompt"
                    type="textarea"
                    rows={3}
                    value={prompt}
                    disabled={!!batch}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="The 2026 FIFA World Cup — its hosts, venues and format"
                  />
                  {/* A dated label here produced a batch of correct questions
                      about the wrong tournaments: "World Cup Final - July 2026"
                      reads as a title rather than a subject, and the generator
                      resolved it into general World Cup history. Naming the
                      subject is the whole fix, so the field says so. */}
                  <FormText>
                    Describe the subject, not the occasion. Name it specifically
                    — <em>the 2026 FIFA World Cup</em> stays on topic, where a
                    dated label like <em>World Cup — Jul 2026</em> reads as a
                    title and drifts into general history.
                  </FormText>
                </FormGroup>

                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="batch-count">Count</Label>
                      <Input
                        id="batch-count"
                        type="number"
                        min={MIN_REQUESTED}
                        max={MAX_REQUESTED}
                        value={requested}
                        disabled={!!batch}
                        invalid={requested !== "" && !countValid}
                        onChange={(e) => setRequested(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={8}>
                    <FormGroup>
                      <Label>Difficulty mix</Label>
                      {DIFFICULTY_ORDER.map((key: ContentDifficulty) => (
                        <div className="d-flex align-items-center" key={key}>
                          <span
                            className="text-capitalize small"
                            style={{ width: 64 }}
                          >
                            {key}
                          </span>
                          <Input
                            type="range"
                            min={0}
                            max={100}
                            step={5}
                            value={mix[key]}
                            disabled={!!batch}
                            aria-label={`${key} share`}
                            className="mx-2 flex-grow-1"
                            onChange={(e) =>
                              setMix(
                                rebalanceMix(mix, key, Number(e.target.value)),
                              )
                            }
                          />
                          <span
                            className="small text-muted text-right"
                            style={{ width: 44 }}
                          >
                            {mix[key]}%
                          </span>
                        </div>
                      ))}
                      <FormText>
                        Steering, not a guarantee — the shares always total
                        100%.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>

                {!batch && (
                  <Button type="submit" color="primary" disabled={!canSubmit}>
                    <i className="fas fa-magic mr-2" />
                    {submitting ? "Starting…" : "Generate"}
                  </Button>
                )}
              </Form>

              {batch && (
                <>
                  <GenerationProgress batch={batch} status={status} />

                  {status === "done" && (
                    <Alert color="success" className="mt-3 mb-3">
                      Batch <strong>“{batch.name}”</strong> created as a draft ·{" "}
                      {batch.generation?.produced ?? 0} questions ·{" "}
                      {batch.generation?.qaPassed ?? 0} passed QA ·{" "}
                      {(batch.generation?.produced ?? 0) -
                        (batch.generation?.qaPassed ?? 0)}{" "}
                      need review
                    </Alert>
                  )}

                  {(status === "failed" || status === "stalled") && (
                    <Alert color="danger" className="mt-3 mb-3">
                      {generationFailureText(batch.generation, status)}
                    </Alert>
                  )}

                  {status !== "running" && (
                    <div className="d-flex align-items-center">
                      <Button
                        color="primary"
                        className="mr-3"
                        onClick={() =>
                          history.push(`/content/drafts/${batch.id}`)
                        }
                      >
                        Open review queue
                        <i className="fas fa-arrow-right ml-2" />
                      </Button>
                      <Button color="link" onClick={generateAgain}>
                        Generate again
                      </Button>
                    </div>
                  )}

                  {status === "running" && (
                    <FormText className="mt-3 d-block">
                      Generation runs in the background — you can leave this
                      page, and the batch will be waiting in{" "}
                      <Button
                        color="link"
                        className="p-0 align-baseline"
                        onClick={() => history.push("/content/drafts")}
                      >
                        Drafts
                      </Button>
                      .
                    </FormText>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};
